import { useQuery, useApolloClient } from '@apollo/client/react'
import { AppContext, AppDispatchContext } from './AppContext'
import { Actions, useAppReducer } from './AppReducer'
import Main from './ui/Main'
import CurrentUser, { CurrentUserSubscription } from './graphql/queries/CurrentUser'
import './App.css'
import { useEffect } from 'react'
import UnauthenticatedApp from './UnauthenticatedApp'
import { ModalStackProvider, useModalStack } from './ui/ModalStack'

const LEGAL_PATHS = ['/privacy', '/terms']

function AppContent({ appState, dispatch }) {
    const { data, subscribeToMore } = useQuery(CurrentUser)
    const { pushModal } = useModalStack()

    useEffect(() => {
        if (data?.user) {
            dispatch(Actions.setCurrentRoom(localStorage.getItem('currentRoom')))
            console.log(`subscribing to user updates`)
            subscribeToMore(({
                document: CurrentUserSubscription,
                updateQuery: (prev, {subscriptionData}) => subscriptionData.data ?? prev
            }))
        }
    }, [data?.user?.id, dispatch, subscribeToMore])

    const client = useApolloClient()

    // Check for verification code or password reset in query string
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const code = params.get('code')
        const userId = params.get('userId')
        const resetToken = params.get('passwordReset')
        const acceptInviteId = params.get('acceptInvite')
        const email = params.get('email')

        if (code && userId) {
            // Clear the query string
            window.history.replaceState({}, document.title, window.location.pathname)
            // Open the verifying modal with both the code and userId
            pushModal('verifying', { code, userId })
        } else if (acceptInviteId && email) {
            // Clear the query string
            window.history.replaceState({}, document.title, window.location.pathname)
            // Open the accepting invite modal with the invite ID and email
            pushModal('acceptingInvite', { inviteId: acceptInviteId, email })
        } else if (resetToken && email) {
            // Clear the query string
            window.history.replaceState({}, document.title, window.location.pathname)
            // Open the reset password modal with the token and email
            pushModal('resetPassword', { resetToken, email })
        } else if (params.get('checkout') === 'success') {
            window.history.replaceState({}, document.title, window.location.pathname)
            // Refetch current user so roles/plan update immediately
            client.refetchQueries({ include: [CurrentUser] })
            pushModal('alert', {
                title: '🎉 You\'re a Resident DJ!',
                text: 'Your subscription is active. Enjoy full DJ access.',
                okText: 'Let\'s go'
            })
        } else if (params.get('checkout') === 'cancelled') {
            window.history.replaceState({}, document.title, window.location.pathname)
            pushModal('alert', {
                text: 'Checkout was cancelled. You can upgrade anytime from your profile.',
                okText: 'OK'
            })
        }
    }, [pushModal])

    const pathname = window.location.pathname.replace(/\/$/, '') || '/'
    return (
        <div>
            { appState.loggedIn && !LEGAL_PATHS.includes(pathname)
                ? <Main />
                : <UnauthenticatedApp /> }
        </div>
    )
}

function App() {

    const [appState, dispatch] = useAppReducer()

    return (
        <div className="App">
            <AppContext.Provider value={appState}>
                <AppDispatchContext.Provider value={dispatch}>
                    <ModalStackProvider>
                        <AppContent appState={appState} dispatch={dispatch} />
                    </ModalStackProvider>
                </AppDispatchContext.Provider>
            </AppContext.Provider>
        </div>
    )
}

export default App
